import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBlog } from "./useHook";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  let normalized = url.replace(/\/api(?=\/)/, "");
  if (typeof window !== "undefined" && window.location?.protocol === "https:" && normalized.startsWith("http://")) {
    normalized = "https:" + normalized.slice(5);
  }
  try {
    return encodeURI(decodeURI(normalized));
  } catch {
    return normalized;
  }
};

const BlogView = () => {
  const { id } = useParams();
  const { getBlogById, addOnHeaderBlog, addPopularBlog, showBlogOnHomeScreen } = useBlog();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopularModal, setShowPopularModal] = useState(false);
  const [showHeaderModal, setShowHeaderModal] = useState(false);
  const [isHomeScreenLoading, setIsHomeScreenLoading] = useState(false);

  const fetchBlog = async () => {
    try {
      const blogData = await getBlogById(id);
      setBlog(blogData);
    } catch (error) {
      console.error("Error fetching blog:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch blog details",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const handlePopularToggle = async () => {
    try {
      const response = await addPopularBlog({ blogId: parseInt(id) });
      if (response) {
        setBlog((prev) => ({ ...prev, isPopular: !prev.isPopular }));
        setShowPopularModal(false);
        Swal.fire({
          title: "Success",
          text: blog.isPopular
            ? "Blog removed from popular"
            : "Blog added to popular",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error toggling popular status:", error);
      Swal.fire({
        title: "Error",
        text: "Error toggling popular status",
        icon: "error",
      });
    }
  };

  const handleHeaderToggle = async () => {
    try {
      const response = await addOnHeaderBlog({ blogId: parseInt(id) });
      if (response) {
        setBlog((prev) => ({ ...prev, isHeader: !prev.isHeader }));
        setShowHeaderModal(false);
        Swal.fire({
          title: "Success",
          text: blog.isHeader
            ? "Blog removed from header"
            : "Blog added to header",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error toggling header status:", error);
      Swal.fire({
        title: "Error",
        text: "Error toggling header status",
        icon: "error",
      });
    }
  };

  const handleHomeScreenItem = async () => {
    if (!blog?.id) return;

    setIsHomeScreenLoading(true);
    const newStatus = !blog.homeScreen;

    try {
      await showBlogOnHomeScreen(blog.id, newStatus ? 1 : 0);
      await fetchBlog();
      Swal.fire({
        title: "Success",
        text: newStatus
          ? "Blog has been added to home screen"
          : "Blog has been removed from home screen",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to update home screen status:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update home screen status. Please try again.",
        icon: "error",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setIsHomeScreenLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Blog not found
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Blog Details</h2>
            <div className="flex gap-4">
              <button
                className={`py-2 px-4 rounded flex items-center space-x-2 ${isHomeScreenLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600"
                  } text-white`}
                onClick={handleHomeScreenItem}
                disabled={isHomeScreenLoading}
              >
                {isHomeScreenLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <span>
                    {blog?.homeScreen
                      ? "Hide from home screen"
                      : "Home page Priority"}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowPopularModal(true)}
                className={`px-6 py-3 rounded-lg transition-all transform hover:scale-105 ${blog.isPopular
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-green-600 hover:bg-green-700 text-white"
                  }`}
              >
                {blog.isPopular ? "Remove from Popular" : "Add to Most Popular"}
              </button>
              <button
                onClick={() => setShowHeaderModal(true)}
                className={`px-6 py-3 rounded-lg transition-all transform hover:scale-105 ${blog.isHeader
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-green-600 hover:bg-green-700 text-white"
                  }`}
              >
                {blog.isHeader ? "Remove from Featured Blog" : "Add Featured Blog"}
              </button>
            </div>
          </div>

          {/* Blog Status and Category */}
          <div className="flex items-center gap-4 mb-6">
            <span
              className={`px-4 py-2 rounded-full text-sm font-medium ${blog.status === "published"
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
                }`}
            >
              {blog.status === "published" ? "Published" : "Draft"}
            </span>
            <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              Category: {blog.category?.name || "Uncategorized"}
            </span>
          </div>
        </div>

        {/* Blog Content */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Header Image */}
          {blog.headerImage && (
            <div className="w-full relative min-h-[280px] bg-gray-100">
              <img
                src={normalizeImageUrl(blog.headerImage)}
                alt={blog.name}
                className="w-full h-full min-h-[280px] object-cover object-center"
              />
            </div>
          )}

          <div className="p-8">
            {/* Blog Title and Description */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {blog.name}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                {blog.description}
              </p>
            </div>

            {/* Blog Content */}
            <div className="prose max-w-none">
              {blog.content?.map((block, index) => (
                <div key={index} className="mb-8">
                  {block.type === "text" ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: block.content }}
                      className="blog-content text-gray-700 leading-relaxed"
                    />
                  ) : (
                    <div className="flex justify-center">
                      <img
                        src={normalizeImageUrl(block.content)}
                        alt={`Blog content ${index + 1}`}
                        className="max-w-full rounded-lg shadow-lg"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                        onLoad={() => { }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Popular Modal */}
      {showPopularModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full transform transition-all">
            <h3 className="text-2xl font-bold mb-4">Confirm Action</h3>
            <p className="mb-6 text-gray-600">
              {blog.isPopular
                ? "Are you sure you want to remove this blog from popular?"
                : "Are you sure you want to add this blog to popular?"}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowPopularModal(false)}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePopularToggle}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Modal */}
      {showHeaderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full transform transition-all">
            <h3 className="text-2xl font-bold mb-4">Confirm Action</h3>
            <p className="mb-6 text-gray-600">
              {blog.isHeader
                ? "Are you sure you want to remove this blog from featured blog?"
                : "Are you sure you want to show this blog on featured blog?"}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowHeaderModal(false)}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleHeaderToggle}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogView;
